import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Paper, IconButton, Chip, Button, Tooltip, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText } from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Visibility as ViewIcon, CheckCircle as CompleteIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import careerEventsService from '../../services/careerEventsService';
import CareerEventDialog from './CareerEventDialog';

const statusColors = {
  Scheduled: 'info',
  InProgress: 'warning',
  Completed: 'success',
  Cancelled: 'default'
};

// Custom Timeline Components (avoiding @mui/lab compatibility issues with MUI v7)
const TimelineContainer = ({ children }) => (
  <Box sx={{ position: 'relative', pl: 3 }}>{children}</Box>
);

const TimelineItemCustom = ({ date, icon, iconColor, isLast, children }) => (
  <Box sx={{ display: 'flex', position: 'relative', pb: isLast ? 0 : 2 }}>
    {/* Vertical line */}
    {!isLast && (
      <Box sx={{
        position: 'absolute',
        left: -16,
        top: 20,
        bottom: 0,
        width: 2,
        bgcolor: 'divider'
      }} />
    )}
    {/* Dot */}
    <Box sx={{
      position: 'absolute',
      left: -24,
      top: 4,
      width: 18,
      height: 18,
      borderRadius: '50%',
      bgcolor: iconColor || 'primary.main',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      fontSize: 10
    }}>
      <span className="material-symbols-outlined" style={{ fontSize: 12 }}>{icon || 'event'}</span>
    </Box>
    {/* Content */}
    <Box sx={{ flex: 1, ml: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        {date}
      </Typography>
      {children}
    </Box>
  </Box>
);

export default function CareerEventsTimeline({ employeeId, personName, readOnly = false, events: externalEvents = null, loading: externalLoading = false }) {
  const { t } = useTranslation();
  const [internalEvents, setInternalEvents] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [internalLoading, setInternalLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [viewMode, setViewMode] = useState(false);

  // Use external events if provided, otherwise use internal state
  const events = externalEvents !== null ? externalEvents : internalEvents;
  const loading = externalEvents !== null ? externalLoading : internalLoading;

  const loadData = useCallback(async () => {
    if (externalEvents !== null) return; // Skip loading if events provided externally
    setInternalLoading(true);
    try {
      const [eventsData, typesData] = await Promise.all([
        careerEventsService.getEmployeeEvents(employeeId),
        careerEventsService.getEventTypes()
      ]);
      setInternalEvents(eventsData || []);
      setEventTypes(typesData || []);
    } catch (err) {
      console.error('Failed to load career events:', err);
    } finally {
      setInternalLoading(false);
    }
  }, [employeeId, externalEvents]);

  useEffect(() => {
    if (employeeId && externalEvents === null) loadData();
  }, [employeeId, loadData, externalEvents]);

  const handleAdd = () => {
    setSelectedEvent(null);
    setViewMode(false);
    setDialogOpen(true);
  };

  const handleEdit = (event) => {
    setSelectedEvent(event);
    setViewMode(false);
    setDialogOpen(true);
  };

  const handleView = (event) => {
    setSelectedEvent(event);
    setViewMode(true);
    setDialogOpen(true);
  };

  const handleDialogClose = (saved) => {
    setDialogOpen(false);
    setSelectedEvent(null);
    if (saved) loadData();
  };

  const [completing, setCompleting] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const handleCompleteFollowUp = async (followUpId) => {
    setCompleting(followUpId);
    try {
      await careerEventsService.completeFollowUp(followUpId);
      loadData();
    } catch (err) {
      console.error('Failed to complete follow-up:', err);
    } finally {
      setCompleting(null);
    }
  };

  const handleDeleteEvent = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await careerEventsService.deleteEvent(deleteConfirm.id);
      setDeleteConfirm(null);
      loadData();
    } catch (err) {
      console.error('Failed to delete event:', err);
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // Only show "select employee" message if no employeeId AND no external events provided
  if (!employeeId && externalEvents === null) {
    return (
      <Paper sx={{ p: 2, textAlign: 'center' }}>
        <Typography color="text.secondary">{t('careerEvents.selectEmployee', 'Select an employee to view career events')}</Typography>
      </Paper>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">{t('careerEvents.title', 'Career Events')}</Typography>
        {!readOnly && (
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={handleAdd}>
            {t('careerEvents.addEvent', 'Add Event')}
          </Button>
        )}
      </Box>

      {loading ? (
        <Typography color="text.secondary">{t('common.loading', 'Loading...')}</Typography>
      ) : events.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">{t('careerEvents.noEvents', 'No career events recorded')}</Typography>
        </Paper>
      ) : (
        <TimelineContainer>
          {events.map((event, index) => (
            <TimelineItemCustom
              key={event.id}
              date={formatDate(event.eventDate)}
              icon={event.eventTypeIcon}
              iconColor={event.eventTypeColor}
              isLast={index === events.length - 1}
            >
              <Paper elevation={1} sx={{ p: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1, mr: 1 }}>
                    <Typography variant="subtitle2">{event.title}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {event.eventTypeName}
                    </Typography>
                    {event.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {event.description}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    {!readOnly && event.visibleToEmployee && (
                      <Chip
                        label={t('careerEvents.visibleToEmployee', 'Visible to the employee')}
                        size="small"
                        variant="outlined"
                        color="success"
                        sx={{ fontSize: '0.7rem', height: 20 }}
                      />
                    )}
                    <Chip label={t(`careerEvents.status.${event.status.charAt(0).toLowerCase() + event.status.slice(1)}`, event.status)} size="small" color={statusColors[event.status] || 'default'} />
                    {!readOnly && (
                      <>
                        <Tooltip title={t('common.view', 'View')}>
                          <IconButton size="small" onClick={() => handleView(event)}><ViewIcon fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title={t('common.edit', 'Edit')}>
                          <IconButton size="small" onClick={() => handleEdit(event)}><EditIcon fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title={t('common.delete', 'Delete')}>
                          <IconButton size="small" color="error" onClick={() => setDeleteConfirm(event)}><DeleteIcon fontSize="small" /></IconButton>
                        </Tooltip>
                      </>
                    )}
                  </Box>
                </Box>
                {event.hasFollowUp && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                    {event.overdueFollowUpsCount > 0 ? (
                      <Chip
                        label={`${event.overdueFollowUpsCount} ${t('careerEvents.followUp.statusValues.overdue', 'overdue')}`}
                        size="small"
                        color="error"
                      />
                    ) : event.pendingFollowUpsCount > 0 ? (
                      <Chip
                        label={`${event.pendingFollowUpsCount} ${t('careerEvents.followUp.statusValues.pending', 'pending')}`}
                        size="small"
                        variant="outlined"
                        color="warning"
                      />
                    ) : (
                      <Chip
                        label={t('careerEvents.followUp.statusValues.completed', 'completed')}
                        size="small"
                        variant="outlined"
                        color="success"
                      />
                    )}
                    {event.followUpDueDate && event.pendingFollowUpsCount > 0 && (
                      <Chip
                        label={`${t('careerEvents.followUp.nextDue', 'Due')}: ${formatDate(event.followUpDueDate)}`}
                        size="small"
                        variant="outlined"
                        color={event.overdueFollowUpsCount > 0 ? 'error' : 'info'}
                      />
                    )}
                  </Box>
                )}
              </Paper>
            </TimelineItemCustom>
          ))}
        </TimelineContainer>
      )}

      <CareerEventDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        event={selectedEvent}
        employeeId={employeeId}
        personName={personName}
        eventTypes={eventTypes}
        viewMode={viewMode}
      />

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>{t('careerEvents.deleteConfirm.title', 'Delete Event')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('careerEvents.deleteConfirm.message', 'Are you sure you want to delete this event? This action cannot be undone.')}
          </DialogContentText>
          {deleteConfirm && (
            <Typography variant="subtitle2" sx={{ mt: 2 }}>
              {deleteConfirm.title} - {formatDate(deleteConfirm.eventDate)}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>{t('common.cancel', 'Cancel')}</Button>
          <Button color="error" variant="contained" onClick={handleDeleteEvent} disabled={deleting}>
            {deleting ? <CircularProgress size={20} /> : t('common.delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

